<template>
  <div id="content">
    <!-- Carousel -->
    <v-carousel hide-controls hide-delimiters dark :height="carouselHeight">
      <v-carousel-item
        v-for="(item, i) in carousel.items"
        :key="i"
        :src="item.src"
        transition="fade-transition"
        reverse-transition="fade-transition"
      ></v-carousel-item>
    </v-carousel>

    <!-- Welcome section -->
    <v-container>
      <v-layout align-center style="min-height: 450px;">
        <v-flex class="text-xs-center">
          <!-- Tagline -->
          <div class="tagline subheading text-xs-center font-italic">
            Reaching out to our community, one family at a time
          </div>
          <h4 class="display-1 section-title mt-5">Welcome!</h4>
          <div class="subheading py-4 mx-auto" style="max-width: 440px;">
            We are union with Christ Community Church, a Bible-believing,
            Scripture-teaching, and Christ-centered Church made up of a group of
            believers lovingly dedicated to fulfilling the Great Commission.
          </div>

          <v-divider class="my-4 mx-auto" style="max-width: 500px;"></v-divider>

          <div class="title mb-3 font-weight-light">Get to know more</div>

          <v-btn to="/about" class="red letter-spacing-2" dark>About Us</v-btn>
        </v-flex>
      </v-layout>
    </v-container>

    <!-- Join Us section -->
    <v-container fluid class="gradient-bg white--text">
      <v-layout align-center style="min-height: 450px;">
        <v-flex class="text-xs-center">
          <h4 class="display-1 section-title my-5">Join Us in Worship!</h4>
          <div class="subheading py-4 mx-auto" style="max-width: 500px;">
            We meet every Sunday from 9:30am - 11:30am at the 6th floor of
            Hemady Square, located at the corner of Hemady St. and E. Rogdriguez
            Ave. in New Manila.
          </div>

          <v-divider
            class="grey lighten-3 my-4 mx-auto"
            style="max-width: 500px;"
          ></v-divider>

          <div class="title mb-3 font-weight-light">Get more detailed</div>

          <v-btn
            href="https://goo.gl/maps/K6uKFx1aYS9TAsai7"
            class="blue letter-spacing-2"
            dark
          >
            Directions
            <v-icon right dark>directions</v-icon>
          </v-btn>
        </v-flex>
      </v-layout>
    </v-container>

    <!-- Church Membership section -->
    <section class="sunset-bg">
      <v-container grid-list-lg class="text-xs-center">
        <v-layout row wrap justify-center>
          <v-flex xs12>
            <v-img
              :src="require('@/assets/church-membership.jpg')"
              height="240"
              contain
            ></v-img>
            <h4 class="display-1 section-title mt-5">Church Membership</h4>
            <div class="subheading py-4 mx-auto" style="max-width: 600px;">
              UCC Church is welcoming people who wants to be a member of our
              community. If you are interested to apply for church membership
              and wish to attend membership classes, kindly email us at
              <a href="mailto:writetouccc@gmail.com">writetouccc@gmail.com</a>.
            </div>
          </v-flex>
          <v-flex v-if="membershipSermons.length !== 0" xs12>
            <div class="subheading py4 mx-auto" style="max-width: 600px;">
              Also, if you would like to know more about church membership, what
              it's like to be a part of God's family, please listen to our
              sermons below.
            </div>
            <v-divider
              class="grey darken-1 my-4 mx-auto"
              style="max-width: 800px;"
            ></v-divider>
            <h3 class="headline my-4">Life in the Family of God Series</h3>
          </v-flex>
          <v-flex v-for="(sermon, j) in membershipSermons" :key="j" xs6 sm4 lg2>
            <v-card flat>
              <v-img
                :src="cover(sermon, 1)"
                :lazy-src="imageNotAvailable"
                :aspect-ratio="4 / 3"
                class="black"
                @error="imageLoadError"
              >
                <v-container fill-height fluid class="overlay">
                  <v-btn dark round flat @click="playSermon(sermon)">
                    <v-icon left>play_circle_outline</v-icon>Play
                  </v-btn>
                </v-container>
              </v-img>
            </v-card>
          </v-flex>
        </v-layout>
      </v-container>
    </section>

    <!-- Latest Sermons and Events -->
    <v-container grid-list-lg>
      <v-layout row wrap justify-center>
        <v-flex xs12 sm6 md6 lg6 xl5>
          <h5 class="title letter-spacing-1 text-uppercase my-4">
            Latest Sermons
          </h5>
          <div v-if="sermons.length !== 0">
            <v-card
              v-for="(sermon, i) in sermons"
              :key="i"
              flat
              @click="playSermon(sermon)"
            >
              <v-layout align-center>
                <v-flex xs4 xl3>
                  <v-img
                    :src="cover(sermon, 1)"
                    :aspect-ratio="4 / 3"
                    :lazy-src="imageNotAvailable"
                    max-height="160px"
                    max-width="200px"
                    class="black"
                    contain
                  ></v-img>
                </v-flex>
                <v-flex xs8 xl7>
                  <v-card-title primary-title>
                    <div>
                      <span class="caption">{{ date(sermon) }}</span>
                      <div class="title font-weight-regular mt-2 mb-1">
                        {{ sermon.title }}
                      </div>
                      <div class="text--secondary">{{ sermon.scripture }}</div>
                    </div>
                  </v-card-title>
                </v-flex>
              </v-layout>
            </v-card>
          </div>
          <v-card v-else height="300" flat>
            <v-layout fill-height align-center>
              <v-flex text-xs-center>
                <v-icon x-large>cloud_off</v-icon>
                <h4 class="headline my-2">
                  Unable to load latest sermons
                </h4>
                <p class="subheading">
                  Something went wrong. Try reloading the page again.
                </p>
                <v-btn color="primary" @click="getLatestSermons"
                  >Try Again</v-btn
                >
              </v-flex>
            </v-layout>
          </v-card>
        </v-flex>
        <v-flex xs12 sm6 md6 lg6 xl5>
          <h5 class="title letter-spacing-1 text-uppercase my-4">
            Latest Events
          </h5>
          <v-list v-if="events.length !== 0" three-line>
            <template v-for="(event, index) in events">
              <v-list-tile :key="index" avatar :to="`/events/${event._id}`">
                <v-list-tile-avatar tile>
                  <div class="date-box">
                    <div class="month">{{ eventMonth(event.startDate) }}</div>
                    <div class="date">{{ eventDay(event.startDate) }}</div>
                  </div>
                </v-list-tile-avatar>

                <v-list-tile-content class="ml-4">
                  <v-list-tile-title>
                    {{ event.title }}
                  </v-list-tile-title>
                  <v-list-tile-sub-title class="text--primary">
                    {{ event.subtitle }}
                  </v-list-tile-sub-title>
                  <v-list-tile-sub-title>
                    {{ event.place }}
                  </v-list-tile-sub-title>
                </v-list-tile-content>
              </v-list-tile>
            </template>
          </v-list>
          <v-card v-else height="300" flat>
            <v-layout fill-height align-center>
              <v-flex text-xs-center>
                <v-icon x-large>cloud_off</v-icon>
                <h4 class="headline my-2">
                  Unable to load latest events
                </h4>
                <p class="subheading">
                  Something went wrong. Try reloading the page again.
                </p>
                <v-btn color="primary" @click="getLatestEvents"
                  >Try Again</v-btn
                >
              </v-flex>
            </v-layout>
          </v-card>
        </v-flex>
      </v-layout>
    </v-container>
  </div>
</template>

<script>
import { bus } from "../main";
import AuthenticationService from "@/services/AuthenticationService";
import ImageMixin from "@/mixins/imageMixin";
import DateMixin from "@/mixins/dateMixin";
export default {
  name: "Home",
  data() {
    return {
      carousel: {
        items: [
          {
            src: require("@/assets/carousel/2020_grow_in_grace1.jpg")
          },
          {
            src: require("@/assets/carousel/2020_grow_in_grace2.jpg")
          },
          {
            src: require("@/assets/carousel/2020_grow_in_grace3.jpg")
          }
        ]
      },
      imageNotAvailable: require("@/assets/image-not-available-3.jpg"),
      membershipSermons: [],
      sermons: [],
      events: []
    };
  },
  mounted() {
    this.getLatestSermons();
    this.getLatestEvents();
    this.getSermonsBySeries("life-in-the-family-of-god");
  },
  methods: {
    async getLatestSermons() {
      let limit = 3;
      const response = await AuthenticationService.getLatestSermons(limit);
      this.sermons = response.data;
    },
    async getLatestEvents() {
      let limit = 4;
      const response = await AuthenticationService.getLatestEvents(limit);
      this.events = response.data;
    },
    async getSermonsBySeries(series) {
      const response = await AuthenticationService.getSermonsBySeries(series);
      this.membershipSermons = response.data;
    },
    playSermon(sermon) {
      bus.$emit("playSermon", sermon);
    }
  },
  mixins: [ImageMixin, DateMixin]
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
.letter-spacing-2 {
  letter-spacing: 0.5em !important;
}
.date-box {
  display: block;
  min-width: 56px;
  min-height: 56px;
  margin-top: 20px;
  padding: 4px;
  box-sizing: border-box;
  border: 1px solid #757575;
  border-radius: 2px;
}
.date-box .month {
  display: block;
  font-size: 16px;
  text-align: center;
  text-transform: uppercase;
}
.date-box .date {
  display: block;
  font-size: 24px;
  text-align: center;
  text-transform: uppercase;
}
.tagline {
  position: relative;
  display: block;
  max-width: 400px;
  margin: auto;
  padding: 0 20px;
  box-sizing: border-box;
  text-align: center;
}
.tagline::before,
.tagline::after {
  content: "";
  display: block;
  position: absolute;
  top: 50%;
  background-color: #ccc;
  height: 1px;
  width: 20px;
}
.tagline::before {
  left: -10px;
}
.tagline::after {
  right: -10px;
}
.gradient-bg {
  background: linear-gradient(to top right, #e66465, #9198e5);
}
.sunset-bg {
  position: relative;
  background-image: url("../assets/sunset-field-of-grain-5980.jpg");
  background-attachment: fixed;
  background-position: center;
  background-size: cover;
}
.sunset-bg::before {
  content: "";
  position: absolute;
  height: 100%;
  width: 100%;
  top: 0;
  left: 0;
  background: rgba(236, 234, 229, 0.8);
}
.sunset-bg div {
  position: relative;
}
.overlay {
  background-color: rgba(0, 0, 0, 0.54);
  opacity: 0;
  transition: opacity 400ms ease;
}
.overlay:hover {
  opacity: 1;
}
</style>



// WEBPACK FOOTER //
// src/components/Home.vue